import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { AlertTriangle, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CountrySelect } from '@/components/ui/country-select';
import LeadFieldMappingTable, {
    type MappingSourceField,
} from '@/components/leads/mapping/LeadFieldMappingTable';
import {
    NO_MAPPING_VALUE,
    validateLeadFieldMapping,
    type LeadFieldMapping,
} from '@/components/leads/mapping/mappingValidation';
import {
    useCampaignFieldCatalog,
    useSaveCampaignMapping,
    useSuggestCampaignMapping,
} from '@/api/campaigns/use-campaigns';
import type { Campaign } from '@/api/campaigns/types';
import {
    combinedTargetsInUse,
    fieldEntriesToRecordMapping,
    moveSourceInCombineOrder,
    recordMappingToFieldEntries,
    type CombineOrder,
    type ValueMaps,
} from '@/components/campaigns/mapping/fieldMappingConversion';
import {
    autoFillValueMap,
    collectSourceValueOptions,
    isEnumLikeTarget,
    targetOptionList,
} from '@/components/campaigns/mapping/valueMapAutoFill';
import CombineOrderPanel from '@/components/campaigns/mapping/CombineOrderPanel';
import ValueMapPanel, {
    type ValueMapTargetGroup,
} from '@/components/campaigns/mapping/ValueMapPanel';

export interface MappingStepProps {
    campaign: Campaign;
    onBack: () => void;
    onAdvance: () => void;
}

const COMBINABLE_TARGETS = ['name', 'notes'];
const REQUIRED_TARGETS = ['name', 'email', 'phone'];

const MappingStep: React.FC<MappingStepProps> = ({ campaign, onBack, onAdvance }) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();

    const { data: catalogData, isLoading: isCatalogLoading } = useCampaignFieldCatalog();
    const availableFields = useMemo(() => catalogData?.data ?? {}, [catalogData]);

    const suggestMapping = useSuggestCampaignMapping();
    const saveMapping = useSaveCampaignMapping();

    const [mapping, setMapping] = useState<LeadFieldMapping>({});
    const [combineOrder, setCombineOrder] = useState<CombineOrder>({});
    const [valueMaps, setValueMaps] = useState<ValueMaps>({});
    const [suggestedMappings, setSuggestedMappings] = useState<Record<string, string | null>>({});
    const [defaultCountry, setDefaultCountry] = useState(campaign.default_country ?? '');
    const [appendUnmapped, setAppendUnmapped] = useState(campaign.append_unmapped_to_notes ?? true);
    const [saveResult, setSaveResult] = useState<{ errors: string[]; warnings: string[] } | null>(
        null
    );

    // Hydrate from the campaign's saved mapping whenever a new version lands
    // (initial load, or after a successful save elsewhere).
    useEffect(() => {
        const hydrated = fieldEntriesToRecordMapping(campaign.field_mapping?.fields);
        setMapping(hydrated.mapping);
        setCombineOrder(hydrated.combineOrder);
        setValueMaps(hydrated.valueMaps);
        setDefaultCountry(campaign.default_country ?? '');
        setAppendUnmapped(campaign.append_unmapped_to_notes ?? true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaign.id, campaign.mapping_version]);

    // Auto-fill the value map for a newly enum-mapped target (field-mapping.md §4.3).
    useEffect(() => {
        if (isCatalogLoading) return;
        const mappedTargets = new Set(
            Object.values(mapping).filter(
                (target): target is string => !!target && target !== NO_MAPPING_VALUE
            )
        );
        mappedTargets.forEach(target => {
            const fieldConfig = availableFields[target];
            if (!isEnumLikeTarget(fieldConfig)) return;

            setValueMaps(prev => {
                if (prev[target]) return prev;
                const sourceKeys = Object.entries(mapping)
                    .filter(([, t]) => t === target)
                    .map(([sourceKey]) => sourceKey);
                const sourceOptions = collectSourceValueOptions(campaign.source_fields, sourceKeys);
                if (sourceOptions.length === 0) return prev;
                const autoFilled = autoFillValueMap(sourceOptions, targetOptionList(fieldConfig));
                if (Object.keys(autoFilled).length === 0) return prev;
                return { ...prev, [target]: autoFilled };
            });
        });
    }, [mapping, availableFields, campaign.source_fields, isCatalogLoading]);

    const mappingSourceFields: MappingSourceField[] = useMemo(
        () =>
            campaign.source_fields.map(field => ({
                key: field.key,
                label: field.label,
                sample: field.sample,
                options: field.options,
            })),
        [campaign.source_fields]
    );

    const metaSourceFields: MappingSourceField[] = useMemo(
        () =>
            (campaign.meta_source_fields ?? []).map(field => ({
                key: field.key,
                label: t(`campaigns.metaAd.fields.${field.key.slice('meta.'.length)}`, {
                    defaultValue: field.label,
                }),
            })),
        [campaign.meta_source_fields, t]
    );

    const sourceFieldLabels = useMemo(() => {
        const labels = campaign.source_fields.reduce<Record<string, string>>((acc, field) => {
            acc[field.key] = field.label || field.key;
            return acc;
        }, {});
        metaSourceFields.forEach(field => {
            labels[field.key] = field.label || field.key;
        });
        return labels;
    }, [campaign.source_fields, metaSourceFields]);

    const handleMappingChange = (sourceKey: string, target: string | null) => {
        setMapping(prev => ({ ...prev, [sourceKey]: target }));
        if (target && COMBINABLE_TARGETS.includes(target)) {
            setCombineOrder(prev => {
                const list = prev[target] ?? [];
                if (list.includes(sourceKey)) return prev;
                return { ...prev, [target]: [...list, sourceKey] };
            });
        }
    };

    const handleMoveCombined = (target: string, sourceKey: string, direction: 'up' | 'down') => {
        setCombineOrder(prev => moveSourceInCombineOrder(prev, target, sourceKey, direction));
    };

    const handleValueMapChange = (
        target: string,
        sourceValueKey: string,
        targetOptionKey: string | null
    ) => {
        setValueMaps(prev => {
            const current = { ...(prev[target] ?? {}) };
            if (targetOptionKey === null) {
                delete current[sourceValueKey];
            } else {
                current[sourceValueKey] = targetOptionKey;
            }
            return { ...prev, [target]: current };
        });
    };

    const handleSuggest = async () => {
        try {
            const result = await suggestMapping.mutateAsync(campaign.id);
            const hydrated = fieldEntriesToRecordMapping(result.fields);
            setMapping(hydrated.mapping);
            setCombineOrder(hydrated.combineOrder);
            setValueMaps(hydrated.valueMaps);

            const flatSuggestions: Record<string, string | null> = {};
            result.fields.forEach(entry => {
                entry.source.forEach(sourceKey => {
                    flatSuggestions[sourceKey] = entry.target;
                });
            });
            setSuggestedMappings(flatSuggestions);
            enqueueSnackbar(t('campaigns.editor.mapping.suggestApplied'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.editor.mapping.suggestFailed'), { variant: 'error' });
        }
    };

    const combinedTargets = useMemo(
        () => combinedTargetsInUse(mapping, COMBINABLE_TARGETS),
        [mapping]
    );

    const valueMapGroups: ValueMapTargetGroup[] = useMemo(() => {
        const mappedTargets = Array.from(
            new Set(
                Object.values(mapping).filter(
                    (target): target is string => !!target && target !== NO_MAPPING_VALUE
                )
            )
        );

        return mappedTargets
            .map(target => {
                const fieldConfig = availableFields[target];
                if (!isEnumLikeTarget(fieldConfig)) return null;
                const sourceKeys = Object.entries(mapping)
                    .filter(([, t]) => t === target)
                    .map(([sourceKey]) => sourceKey);
                const sourceOptions = collectSourceValueOptions(campaign.source_fields, sourceKeys);
                if (sourceOptions.length === 0) return null;
                return {
                    target,
                    targetLabel: fieldConfig?.label ?? target,
                    sourceOptions,
                    targetOptions: targetOptionList(fieldConfig),
                } satisfies ValueMapTargetGroup;
            })
            .filter((group): group is ValueMapTargetGroup => group !== null);
    }, [mapping, availableFields, campaign.source_fields]);

    const validation = useMemo(
        () =>
            validateLeadFieldMapping(
                mapping,
                availableFields,
                REQUIRED_TARGETS,
                campaign.source_fields.length,
                COMBINABLE_TARGETS
            ),
        [mapping, availableFields, campaign.source_fields.length]
    );

    const handleContinue = async () => {
        const fields = recordMappingToFieldEntries({
            mapping,
            combineOrder,
            valueMaps,
            combinableTargets: COMBINABLE_TARGETS,
        });

        try {
            const result = await saveMapping.mutateAsync({
                id: campaign.id,
                data: {
                    fields,
                    default_country: defaultCountry || null,
                    append_unmapped_to_notes: appendUnmapped,
                },
            });
            setSaveResult({ errors: result.errors ?? [], warnings: result.warnings ?? [] });
            enqueueSnackbar(t('campaigns.editor.mapping.saved'), { variant: 'success' });
            onAdvance();
        } catch {
            enqueueSnackbar(t('campaigns.editor.mapping.saveFailed'), { variant: 'error' });
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <div>
                        <CardTitle>{t('campaigns.editor.mapping.title')}</CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {t('campaigns.editor.mapping.description')}
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSuggest}
                        disabled={suggestMapping.isPending || isCatalogLoading}
                    >
                        {suggestMapping.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                            <Sparkles className="h-4 w-4 mr-2" />
                        )}
                        {t('campaigns.editor.mapping.suggestButton')}
                    </Button>
                </CardHeader>
                <CardContent>
                    {validation.errors.length > 0 && (
                        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                            <div className="mb-2 flex items-center">
                                <AlertTriangle className="mr-2 h-5 w-5 text-red-600 dark:text-red-400" />
                                <h4 className="font-medium text-red-800 dark:text-red-300">
                                    {t('campaigns.editor.mapping.issuesTitle')}
                                </h4>
                            </div>
                            <ul className="space-y-1 text-sm text-red-700 dark:text-red-400">
                                {validation.errors.map((error, index) => (
                                    <li key={index}>• {error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {validation.isValid && (
                        <div className="mb-4 flex items-center rounded-lg border border-green-500/30 bg-green-500/10 p-4">
                            <CheckCircle className="mr-2 h-5 w-5 text-green-600 dark:text-green-400" />
                            <span className="font-medium text-green-800 dark:text-green-300">
                                {t('campaigns.editor.mapping.allRequiredMapped')}
                            </span>
                        </div>
                    )}

                    {isCatalogLoading ? (
                        <p className="text-sm text-muted-foreground">{t('table.loading')}</p>
                    ) : (
                        <LeadFieldMappingTable
                            sourceFields={mappingSourceFields}
                            availableFields={availableFields}
                            mapping={mapping}
                            onChange={handleMappingChange}
                            suggestedMappings={suggestedMappings}
                            combinableTargets={COMBINABLE_TARGETS}
                        />
                    )}
                </CardContent>
            </Card>

            {metaSourceFields.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">
                            {t('campaigns.editor.mapping.metaAdTitle')}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground">
                            {t('campaigns.editor.mapping.metaAdDescription')}
                        </p>
                    </CardHeader>
                    <CardContent>
                        <LeadFieldMappingTable
                            sourceFields={metaSourceFields}
                            availableFields={availableFields}
                            mapping={mapping}
                            onChange={handleMappingChange}
                            suggestedMappings={suggestedMappings}
                            combinableTargets={COMBINABLE_TARGETS}
                        />
                    </CardContent>
                </Card>
            )}

            <CombineOrderPanel
                targets={combinedTargets}
                mapping={mapping}
                combineOrder={combineOrder}
                availableFields={availableFields}
                sourceFieldLabels={sourceFieldLabels}
                onMove={handleMoveCombined}
            />

            <ValueMapPanel
                groups={valueMapGroups}
                valueMaps={valueMaps}
                onChange={handleValueMapChange}
            />

            <Card>
                <CardHeader>
                    <CardTitle className="text-base">
                        {t('campaigns.editor.mapping.optionsTitle')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <Label>{t('campaigns.editor.mapping.defaultCountry')}</Label>
                        <CountrySelect
                            value={defaultCountry}
                            onChange={setDefaultCountry}
                            placeholder={t('campaigns.editor.mapping.defaultCountryPlaceholder')}
                            className="max-w-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                            {t('campaigns.editor.mapping.defaultCountryHint')}
                        </p>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border p-3">
                        <div>
                            <Label htmlFor="append-unmapped">
                                {t('campaigns.editor.mapping.appendUnmapped')}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                                {t('campaigns.editor.mapping.appendUnmappedHint')}
                            </p>
                        </div>
                        <Switch
                            id="append-unmapped"
                            checked={appendUnmapped}
                            onCheckedChange={setAppendUnmapped}
                        />
                    </div>
                </CardContent>
            </Card>

            {saveResult && (saveResult.errors.length > 0 || saveResult.warnings.length > 0) && (
                <Card>
                    <CardContent className="space-y-2 pt-6">
                        {saveResult.errors.map((error, index) => (
                            <p
                                key={`e-${index}`}
                                className="text-sm text-red-600 dark:text-red-400"
                            >
                                • {error}
                            </p>
                        ))}
                        {saveResult.warnings.map((warning, index) => (
                            <p
                                key={`w-${index}`}
                                className="text-sm text-amber-600 dark:text-amber-400"
                            >
                                • {warning}
                            </p>
                        ))}
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-between border-t pt-4">
                <Button variant="outline" onClick={onBack}>
                    {t('campaigns.editor.actions.back')}
                </Button>
                <Button onClick={handleContinue} disabled={saveMapping.isPending}>
                    {saveMapping.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {t('campaigns.editor.actions.saveAndContinue')}
                </Button>
            </div>
        </div>
    );
};

export default MappingStep;
